import { UserAvatar } from '@clarix/ui-shared';
import { cn } from '@clarix/ui/lib/cn';
import { CHIP } from './constants';
import { CHIP_COPY } from './copy';
import type { ChipPerson } from './types';

/** `pulse-dot-anim` is declared outside Tailwind's layers, so only an important override stops it. */
export function PulseDot() {
  return (
    <span
      className="pulse-dot-anim h-1.5 w-1.5 shrink-0 rounded-full bg-current motion-reduce:!animate-none"
      aria-hidden
    />
  );
}

const FACE_SIZE = { sm: 'h-5 w-5', md: 'h-6 w-6' } as const;

interface FacesProps {
  people: ChipPerson[];
  size?: keyof typeof FACE_SIZE;
  /** Off where the rest are named in words beside the stack. */
  showMore?: boolean;
  className?: string;
}

/** Decorative: whoever renders it names the people (aria-label or text). */
export function Faces({ people, size = 'md', showMore = true, className }: FacesProps) {
  if (people.length === 0) return null;
  const shown = people.slice(0, CHIP.FACES_MAX);
  const more = people.length - shown.length;
  return (
    <span className={cn('flex shrink-0 items-center', className)} aria-hidden>
      {shown.map((p, i) => (
        <UserAvatar
          key={p.id}
          name={p.name}
          src={p.avatarUrl}
          className={cn(FACE_SIZE[size], 'ring-2 ring-background', i > 0 && '-ml-1.5')}
          fallbackClassName="text-xxs"
        />
      ))}
      {showMore && more > 0 && (
        <span
          className={cn(
            'bg-muted text-muted-foreground ring-background -ml-1.5 flex items-center justify-center rounded-full px-1 text-xxs font-semibold ring-2',
            size === 'md' ? 'h-6 min-w-6' : 'h-5 min-w-5',
          )}
        >
          {CHIP_COPY.morePeople(more)}
        </span>
      )}
    </span>
  );
}
